import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  ShoppingBag, 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Check, 
  X, 
  Truck, 
  Package,
  CreditCard,
  Banknote,
  RefreshCw,
  Users,
  AlertCircle,
  Settings,
  UtensilsCrossed,
  Lock,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  TrendingUp,
  Euro,
  BarChart3,
  CalendarDays,
  History,
  CheckCircle2,
  XCircle,
  Filter,
  Search,
  Pause,
  Play,
  Download,
  AlertTriangle,
  FileSpreadsheet,
  Timer
} from 'lucide-react';
import { Tag } from 'lucide-react';
import { Printer } from 'lucide-react';
import AdminPromoCodes from './AdminPromoCodes';
import { printOrderTicket } from '../lib/printTicket';
import { format, isToday, isTomorrow, isAfter, parseISO, startOfDay, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isAuthenticated = () => {
  const token = localStorage.getItem('admin_token');
  return token === 'authenticated';
};

const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('pending');
  const [orderSearch, setOrderSearch] = useState('');

  // Data states
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [deliverySettings, setDeliverySettings] = useState({
    delivery_time_minutes: 40,
    delivery_enabled: true,
    delivery_radius_km: 10,
    min_order_amount: 20,
    delivery_message: 'Livraison estimée',
    orders_enabled: true,
    orders_paused_reason: '',
  });
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [postalCodes, setPostalCodes] = useState([]);
  const [newPostalCode, setNewPostalCode] = useState('');
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [nowTick, setNowTick] = useState(Date.now());

  // Track known order/reservation IDs across polls so EVERY new entry triggers a sound
  const knownOrderIdsRef = useRef(null);
  const knownReservationIdsRef = useRef(null);
  // Continuous-ringing loops: keep playing until pending items are validated
  const orderLoopRef = useRef(null);
  const reservationLoopRef = useRef(null);
  const [soundMuted, setSoundMuted] = useState(false);
  const soundMutedRef = useRef(false);
  useEffect(() => { soundMutedRef.current = soundMuted; }, [soundMuted]);

  // Shared AudioContext (browser autoplay policy requires reuse after a user gesture)
  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const getAudioContext = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };
  const unlockAudio = () => {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    audioUnlockedRef.current = true;
  };

  // Modals
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [estimatedDelay, setEstimatedDelay] = useState('45');
  const [isDelayUpdate, setIsDelayUpdate] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null); // { type: 'order'|'reservation', id }
  const [cancelReason, setCancelReason] = useState('');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', description: '', price: '', category: '', has_pasta_choice: false });
  const [newCategory, setNewCategory] = useState({ id: '', label: '', icon: '🍽️' });
  const [reservationFilter, setReservationFilter] = useState('upcoming'); // upcoming | confirmed | pending | cancelled | all
  const [reservationBlocks, setReservationBlocks] = useState([]);
  const [newBlock, setNewBlock] = useState({ date: '', end_date: '', period: 'dinner', reason: '' });

  const CANCEL_REASONS = [
    'Produit en rupture de stock',
    'Hors zone de livraison',
    'Restaurant fermé',
    'Trop de commandes en attente',
    'Heure souhaitée indisponible',
    'Adresse incorrecte / impossible à livrer',
    'Paiement non validé',
    'Autre (préciser)',
  ];

  useEffect(() => {
    if (authenticated) {
      fetchAllData();
      const interval = setInterval(() => {
        fetchOrdersAndReservations();
      }, 10000);
      const tickInterval = setInterval(() => setNowTick(Date.now()), 30000);
      return () => { clearInterval(interval); clearInterval(tickInterval); };
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && (activeTab === 'stats' || activeTab === 'clients' || activeTab === 'dashboard')) {
      fetchStatsAndCustomers();
    }
  }, [authenticated, activeTab]);

  // ---- Continuous ringing while pending items exist ----
  // Orders: ring every 8s while at least one pending order remains
  useEffect(() => {
    if (!authenticated) return;
    const pendingCount = orders.filter(o => o.status === 'pending').length;
    if (pendingCount > 0) {
      if (!orderLoopRef.current) {
        orderLoopRef.current = setInterval(() => {
          playNotificationSound();
        }, 8000);
      }
    } else if (orderLoopRef.current) {
      clearInterval(orderLoopRef.current);
      orderLoopRef.current = null;
    }
    return () => {
      if (orderLoopRef.current && orders.filter(o => o.status === 'pending').length === 0) {
        clearInterval(orderLoopRef.current);
        orderLoopRef.current = null;
      }
    };
  }, [authenticated, orders]);

  // Reservations: ring every 10s while at least one pending reservation remains
  useEffect(() => {
    if (!authenticated) return;
    const pendingCount = reservations.filter(r => r.status === 'pending').length;
    if (pendingCount > 0) {
      if (!reservationLoopRef.current) {
        reservationLoopRef.current = setInterval(() => {
          playReservationSound();
        }, 10000);
      }
    } else if (reservationLoopRef.current) {
      clearInterval(reservationLoopRef.current);
      reservationLoopRef.current = null;
    }
    return () => {
      if (reservationLoopRef.current && reservations.filter(r => r.status === 'pending').length === 0) {
        clearInterval(reservationLoopRef.current);
        reservationLoopRef.current = null;
      }
    };
  }, [authenticated, reservations]);

  // Cleanup loops on unmount/logout
  useEffect(() => {
    return () => {
      if (orderLoopRef.current) clearInterval(orderLoopRef.current);
      if (reservationLoopRef.current) clearInterval(reservationLoopRef.current);
    };
  }, []);

  // Unlock the audio context on the first user gesture (click / keydown / touch).
  // This is required by browser autoplay policies — a suspended context cannot play until resumed.
  useEffect(() => {
    if (!authenticated) return;
    if (audioUnlockedRef.current) return;
    const handler = () => {
      unlockAudio();
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
    };
    window.addEventListener('click', handler, { once: false });
    window.addEventListener('keydown', handler, { once: false });
    window.addEventListener('touchstart', handler, { once: false });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
    };
  }, [authenticated]);

  const handleLogin = async () => {
    // Unlock the audio context on this user gesture so future sound loops can play
    // (browsers block audio until a user interaction has resumed the context)
    unlockAudio();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem('admin_token', 'authenticated');
        setAuthenticated(true);
        setLoginError('');
      }
    } catch (error) {
      setLoginError('Mot de passe incorrect');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAuthenticated(false);
  };

  const playNotificationSound = () => {
    if (soundMutedRef.current) return;
    try {
      const audioContext = getAudioContext();
      if (!audioContext) return;
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
      const now = audioContext.currentTime;

      // ORDER chime — bright ascending major triad (C5 → E5 → G5), played 2x for urgency
      const notes = [
        { freq: 523.25, start: 0.0,  duration: 0.35 }, // C5
        { freq: 659.25, start: 0.18, duration: 0.35 }, // E5
        { freq: 783.99, start: 0.36, duration: 0.55 }, // G5
        // Second pass louder so it can't be missed
        { freq: 523.25, start: 1.00, duration: 0.30 },
        { freq: 659.25, start: 1.18, duration: 0.30 },
        { freq: 783.99, start: 1.36, duration: 0.60 },
      ];

      notes.forEach(({ freq, start, duration }) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(0.45, now + start + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
        osc.connect(gain);
        gain.connect(audioContext.destination);
        osc.start(now + start);
        osc.stop(now + start + duration);
      });
    } catch (e) {}
  };

  const playReservationSound = () => {
    if (soundMutedRef.current) return;
    try {
      const audioContext = getAudioContext();
      if (!audioContext) return;
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
      const now = audioContext.currentTime;

      // RESERVATION chime — softer descending bell-like pattern (A5 → F5 → D5)
      // Distinctly different from the order chime (descending instead of ascending, triangle wave)
      const notes = [
        { freq: 880.00, start: 0.0,  duration: 0.45 }, // A5
        { freq: 698.46, start: 0.30, duration: 0.45 }, // F5
        { freq: 587.33, start: 0.60, duration: 0.70 }, // D5
      ];

      notes.forEach(({ freq, start, duration }) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(0.40, now + start + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
        osc.connect(gain);
        gain.connect(audioContext.destination);
        osc.start(now + start);
        osc.stop(now + start + duration);
      });
    } catch (e) {}
  };

  const fetchOrdersAndReservations = async () => {
    try {
      const [ordersRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/reservations`)
      ]);

      const newOrders = ordersRes.data || [];
      const newReservations = reservationsRes.data || [];

      // First load: seed known IDs without playing sound
      if (knownOrderIdsRef.current === null) {
        knownOrderIdsRef.current = new Set(newOrders.map(o => o.id));
      } else {
        // Detect ANY freshly-appeared order regardless of status
        // (card orders may arrive already 'paid' but must still ring)
        const freshOrders = newOrders.filter(o => !knownOrderIdsRef.current.has(o.id));
        if (freshOrders.length > 0) {
          toast.info(`🔔 ${freshOrders.length} nouvelle${freshOrders.length > 1 ? 's' : ''} commande${freshOrders.length > 1 ? 's' : ''} !`, { duration: 5000 });
          playNotificationSound();
        }
        // Update the known set with the full current list
        knownOrderIdsRef.current = new Set(newOrders.map(o => o.id));
      }

      if (knownReservationIdsRef.current === null) {
        knownReservationIdsRef.current = new Set(newReservations.map(r => r.id));
      } else {
        const freshReservations = newReservations.filter(r =>
          r.status === 'pending' && !knownReservationIdsRef.current.has(r.id)
        );
        if (freshReservations.length > 0) {
          toast.info(`🍽️ ${freshReservations.length} nouvelle${freshReservations.length > 1 ? 's' : ''} réservation${freshReservations.length > 1 ? 's' : ''} !`, { duration: 5000 });
          playReservationSound();
        }
        knownReservationIdsRef.current = new Set(newReservations.map(r => r.id));
      }

      setOrders(newOrders);
      setReservations(newReservations);
    } catch (error) {
      console.error('Error fetching orders/reservations:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [ordersRes, reservationsRes, settingsRes, menuRes, categoriesRes, postalCodesRes] = await Promise.all([
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/reservations`),
        axios.get(`${API}/delivery/settings`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/delivery/postal-codes`)
      ]);
      setOrders(ordersRes.data);
      setReservations(reservationsRes.data);
      // Seed known IDs on initial load so existing orders don't trigger a sound
      knownOrderIdsRef.current = new Set((ordersRes.data || []).map(o => o.id));
      knownReservationIdsRef.current = new Set((reservationsRes.data || []).map(r => r.id));
      setDeliverySettings(settingsRes.data);
      setMenuItems(menuRes.data);
      setCategories(categoriesRes.data);
      setPostalCodes(postalCodesRes.data.codes || []);
      if (categoriesRes.data.length > 0 && !selectedCategory) {
        setSelectedCategory(categoriesRes.data[0].id);
      }
      fetchStatsAndCustomers();
      fetchReservationBlocks();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatsAndCustomers = async () => {
    try {
      const [statsRes, customersRes] = await Promise.all([
        axios.get(`${API}/admin/stats/summary`),
        axios.get(`${API}/admin/customers`),
      ]);
      setStats(statsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const downloadExport = (endpoint, filename) => {
    // Open in new tab so browser handles the file download naturally
    const url = `${API}${endpoint}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Export téléchargé !');
  };


  const fetchReservationBlocks = async () => {
    try {
      const res = await axios.get(`${API}/admin/reservation-blocks`);
      setReservationBlocks(res.data || []);
    } catch (e) {
      console.error('fetch blocks failed', e);
    }
  };

  const handleAddReservationBlock = async () => {
    if (!newBlock.date) {
      toast.error('Sélectionne une date de début');
      return;
    }
    if (newBlock.end_date && newBlock.end_date < newBlock.date) {
      toast.error('La date de fin doit être ≥ la date de début');
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/reservation-blocks`, {
        date: newBlock.date,
        end_date: newBlock.end_date || null,
        period: newBlock.period,
        reason: newBlock.reason || null,
      });
      toast.success(res.data?.message || 'Blocage enregistré');
      setNewBlock({ date: '', end_date: '', period: 'dinner', reason: '' });
      fetchReservationBlocks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création du blocage');
    }
  };

  const handleDeleteReservationBlock = async (blockId) => {
    try {
      await axios.delete(`${API}/admin/reservation-blocks/${blockId}`);
      toast.success('Blocage supprimé');
      fetchReservationBlocks();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };


  const toggleOrdersEnabled = async (enabled, reason = '') => {
    try {
      // Optimistic update so the banner hides/shows immediately
      setDeliverySettings((prev) => ({
        ...prev,
        orders_enabled: enabled,
        orders_paused_reason: enabled ? '' : reason,
      }));
      await axios.post(`${API}/delivery/settings`, {
        orders_enabled: enabled,
        orders_paused_reason: enabled ? '' : reason,
      });
      toast.success(enabled ? '✅ Commandes rouvertes !' : '⏸️ Commandes mises en pause.');
      setShowPauseModal(false);
      setPauseReason('');
    } catch (error) {
      // Roll back optimistic update on error
      setDeliverySettings((prev) => ({ ...prev, orders_enabled: !enabled }));
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Determines if an order is scheduled for a future day (not today)
  const isScheduledOrder = (order) => {
    if (!order || !order.order_date) return false;
    try {
      const d = parseISO(order.order_date);
      return !isToday(d);
    } catch {
      return false;
    }
  };

  // Determines if the client picked a specific desired time (vs ASAP/empty)
  const hasDesiredTime = (order) => {
    const t = order?.desired_time;
    return t && t !== 'asap' && t.trim() !== '';
  };

  // Order management
  const handleOrderStatus = async (orderId, status, options = {}) => {
    // Intercept cancellation to ask for a reason
    if (status === 'cancelled') {
      setCancelTarget({ type: 'order', id: orderId });
      setCancelReason('');
      setCustomCancelReason('');
      setShowCancelModal(true);
      return;
    }
    // Intercept confirmation to ask for an estimated delay (unless skip flag set)
    if (status === 'confirmed' && !options.skipDelayPrompt) {
      const order = orders.find(o => o.id === orderId);
      setConfirmTarget(order || { id: orderId });
      // For scheduled (future) orders OR orders where the client picked a specific time today,
      // no delay needed — the desired time IS the validated time
      if (order && (isScheduledOrder(order) || hasDesiredTime(order))) {
        setEstimatedDelay('');
      } else {
        // ASAP / no specific time: pre-fill a reasonable delay
        setEstimatedDelay(order?.order_type === 'livraison' ? '45' : '20');
      }
      setIsDelayUpdate(false);
      setShowConfirmModal(true);
      return;
    }
    try {
      const payload = { status };
      if (options.estimated_delay_minutes) payload.estimated_delay_minutes = options.estimated_delay_minutes;
      await axios.put(`${API}/admin/orders/${orderId}/status`, payload);
      toast.success(status === 'confirmed' ? 'Commande confirmée !' : 'Commande terminée !');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const submitConfirmation = async () => {
    if (!confirmTarget) return;
    const scheduled = isScheduledOrder(confirmTarget);
    const desiredTimePicked = hasDesiredTime(confirmTarget);
    const delayNum = parseInt(estimatedDelay, 10);
    try {
      const payload = { status: 'confirmed' };
      // Only include estimated_delay_minutes when:
      // - it's an immediate order WITHOUT a specific desired time (ASAP), OR
      // - admin explicitly entered a delay (override)
      const useDelay = !scheduled && (!desiredTimePicked || (delayNum && delayNum > 0));
      if (useDelay && delayNum && delayNum > 0) {
        payload.estimated_delay_minutes = delayNum;
      }
      if (isDelayUpdate) payload.is_delay_update = true;
      await axios.put(`${API}/admin/orders/${confirmTarget.id}/status`, payload);
      toast.success(isDelayUpdate ? 'Délai mis à jour — client notifié !' : 'Commande confirmée — client notifié !');
      setShowConfirmModal(false);
      setConfirmTarget(null);
      setEstimatedDelay('');
      setIsDelayUpdate(false);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la confirmation');
    }
  };

  const openUpdateDelayModal = (order) => {
    setConfirmTarget(order);
    setEstimatedDelay(String(order.estimated_delay_minutes || (order.order_type === 'livraison' ? 45 : 20)));
    setIsDelayUpdate(true);
    setShowConfirmModal(true);
  };

  const handleReservationStatus = async (reservationId, status) => {
    if (status === 'cancelled') {
      setCancelTarget({ type: 'reservation', id: reservationId });
      setCancelReason('');
      setCustomCancelReason('');
      setShowCancelModal(true);
      return;
    }
    try {
      await axios.put(`${API}/admin/reservations/${reservationId}/status`, { status });
      toast.success('Réservation confirmée !');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm('⚠️ Supprimer définitivement cette commande ? Cette action est irréversible et la commande disparaîtra de l\'historique.')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      toast.success('Commande supprimée définitivement.');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (!confirm('⚠️ Supprimer définitivement cette réservation ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/admin/reservations/${reservationId}`);
      toast.success('Réservation supprimée définitivement.');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const submitCancellation = async () => {
    if (!cancelTarget) return;
    const finalReason = cancelReason === 'Autre (préciser)' ? customCancelReason.trim() : cancelReason;
    if (!finalReason) {
      toast.error('Veuillez choisir un motif d\'annulation');
      return;
    }
    try {
      const endpoint =
        cancelTarget.type === 'order'
          ? `${API}/admin/orders/${cancelTarget.id}/status`
          : `${API}/admin/reservations/${cancelTarget.id}/status`;
      await axios.put(endpoint, { status: 'cancelled', cancel_reason: finalReason });
      toast.success(cancelTarget.type === 'order' ? 'Commande refusée, client informé.' : 'Réservation refusée, client informé.');
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelReason('');
      setCustomCancelReason('');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  // Delivery & Menu management
  const saveDeliverySettings = async () => {
    try {
      await axios.post(`${API}/delivery/settings`, deliverySettings);
      toast.success('Paramètres enregistrés !');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const savePostalCodes = async (codes) => {
    try {
      await axios.post(`${API}/delivery/postal-codes`, { codes });
      toast.success('Codes postaux mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleItemAvailability = async (itemId) => {
    try {
      await axios.post(`${API}/menu/toggle-availability/${itemId}`);
      fetchAllData();
      toast.success('Disponibilité mise à jour');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name || !newItem.price || !newItem.category) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      await axios.post(`${API}/menu/items`, { ...newItem, price: parseFloat(newItem.price) });
      toast.success('Plat ajouté !');
      setShowAddItemModal(false);
      setNewItem({ name: '', description: '', price: '', category: '', has_pasta_choice: false });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleEditItem = async () => {
    if (!editingItem) return;
    try {
      await axios.put(`${API}/menu/items/${editingItem.id}`, {
        name: editingItem.name,
        description: editingItem.description,
        price: parseFloat(editingItem.price),
        category: editingItem.category,
        has_pasta_choice: editingItem.has_pasta_choice
      });
      toast.success('Plat modifié !');
      setShowEditItemModal(false);
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteItem = async (itemId, isCustom) => {
    if (!confirm('Supprimer ce plat ?')) return;
    try {
      if (isCustom) {
        await axios.delete(`${API}/menu/items/${itemId}`);
      } else {
        await axios.post(`${API}/menu/items/${itemId}/hide`);
      }
      toast.success('Plat supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.label) {
      toast.error('Veuillez entrer un nom de catégorie');
      return;
    }
    const categoryId = newCategory.id || newCategory.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    try {
      await axios.post(`${API}/categories`, { id: categoryId, label: newCategory.label, icon: newCategory.icon });
      toast.success('Catégorie ajoutée !');
      setShowCategoryModal(false);
      setNewCategory({ id: '', label: '', icon: '🍽️' });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Supprimer cette catégorie ?')) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      toast.success('Catégorie supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Helper functions
  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>,
      confirmed: <Badge className="bg-blue-100 text-blue-800">Confirmé</Badge>,
      completed: <Badge className="bg-green-100 text-green-800">Terminé</Badge>,
      cancelled: <Badge className="bg-red-100 text-red-800">Annulé</Badge>,
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getDateLabel = (dateStr) => {
    if (!dateStr) return "Aujourd'hui";
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return "Aujourd'hui";
      if (isTomorrow(date)) return "Demain";
      return format(date, 'EEEE d MMM', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  // Group an array of items by their date field. Returns ordered groups [{ key, label, items }]
  // Past dates first descending? No — most relevant FIRST: today, tomorrow, future asc, then past desc.
  const groupByDate = (list, dateKey = 'order_date') => {
    const map = new Map();
    for (const it of list) {
      const k = it[dateKey] || 'unknown';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    const groups = Array.from(map.entries()).map(([key, items]) => {
      let sortValue = 99999999;
      try {
        const d = parseISO(key);
        sortValue = d.getTime();
      } catch {}
      return { key, label: getDateLabel(key), items, sortValue };
    });
    // Sort: today first, then upcoming ascending, then past descending
    const todayMs = new Date(new Date().toDateString()).getTime();
    groups.sort((a, b) => {
      const aFuture = a.sortValue >= todayMs;
      const bFuture = b.sortValue >= todayMs;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      return aFuture ? a.sortValue - b.sortValue : b.sortValue - a.sortValue;
    });
    return groups;
  };

  // Minutes elapsed since order was created — used to flag stale pending orders
  const getMinutesSinceCreated = (order) => {
    if (!order?.created_at) return 0;
    try {
      const created = parseISO(order.created_at);
      return Math.max(0, Math.floor((nowTick - created.getTime()) / 60000));
    } catch {
      return 0;
    }
  };

  const getRelativeTimeLabel = (order) => {
    const m = getMinutesSinceCreated(order);
    if (m < 1) return "à l'instant";
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h} h`;
    return `il y a ${Math.floor(h / 24)} j`;
  };

  // Filter orders
  const todayOrders = orders.filter(o => {
    const orderDate = o.order_date ? parseISO(o.order_date) : new Date();
    return isToday(orderDate) || !o.order_date;
  });
  
  const futureOrders = orders.filter(o => {
    if (!o.order_date) return false;
    const orderDate = parseISO(o.order_date);
    return isAfter(orderDate, startOfDay(new Date())) && !isToday(orderDate);
  });

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const confirmedOrders = orders.filter(o => o.status === 'confirmed');
  const completedOrders = orders.filter(o => o.status === 'completed');
  const pendingReservations = reservations.filter(r => r.status === 'pending');
  const filteredMenuItems = menuItems.filter(item => item.category === selectedCategory);

  // Stale pending orders (> 15 minutes without action)
  const stalePendingCount = pendingOrders.filter(o => getMinutesSinceCreated(o) > 15).length;

  // Stats calculations
  const todayRevenue = todayOrders.filter(o => o.status === 'completed' || o.status === 'confirmed').reduce((sum, o) => sum + (o.total || 0), 0);
  const todayOrdersCount = todayOrders.length;
  const avgOrderValue = todayOrdersCount > 0 ? todayRevenue / todayOrdersCount : 0;
  
  // Last 7 days stats
  const last7DaysOrders = orders.filter(o => {
    if (!o.created_at) return false;
    const orderDate = parseISO(o.created_at);
    return isAfter(orderDate, subDays(new Date(), 7));
  });
  const weekRevenue = last7DaysOrders.filter(o => o.status === 'completed' || o.status === 'confirmed').reduce((sum, o) => sum + (o.total || 0), 0);

  // Filter orders based on selected filter + search query
  const matchesSearch = (o) => {
    if (!orderSearch.trim()) return true;
    const q = orderSearch.toLowerCase().trim();
    return (
      (o.customer_name || '').toLowerCase().includes(q) ||
      (o.customer_phone || '').toLowerCase().includes(q) ||
      (o.customer_email || '').toLowerCase().includes(q) ||
      (o.id || '').toLowerCase().includes(q) ||
      (o.delivery_address || '').toLowerCase().includes(q)
    );
  };

  const getFilteredOrders = () => {
    let base;
    switch (orderFilter) {
      case 'pending': base = pendingOrders; break;
      case 'confirmed': base = confirmedOrders; break;
      case 'today': base = todayOrders; break;
      case 'future': base = futureOrders; break;
      case 'completed': base = completedOrders; break;
      default: base = orders;
    }
    return base.filter(matchesSearch);
  };

  const filteredCustomers = customers.filter((c) => {
    if (!customerSearch.trim()) return true;
    const q = customerSearch.toLowerCase().trim();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  });

  // Login Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F5F2EA] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#C62E2E] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="font-['Playfair_Display'] text-2xl">Administration</CardTitle>
            <p className="text-[#57534E]">Nota Bene Sympho</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    placeholder="Entrez le mot de passe"
                    className="pr-10"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
              <Button onClick={handleLogin} className="w-full bg-[#C62E2E] hover:bg-[#a02424]" data-testid="admin-login-btn">
                Se connecter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#F5F2EA] pt-24 pb-12">
      {/* Orders paused banner */}
      {!deliverySettings.orders_enabled && (
        <div className="fixed top-[88px] left-1/2 -translate-x-1/2 z-40 max-w-md bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm" data-testid="orders-paused-banner">
          <Pause className="w-4 h-4" />
          <span className="font-medium">Commandes en pause</span>
          {deliverySettings.orders_paused_reason && (
            <span className="opacity-90 hidden sm:inline">— {deliverySettings.orders_paused_reason}</span>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917]">
              Administration
            </h1>
            <p className="text-[#57534E] text-sm">Nota Bene Sympho • Auto-refresh 10s</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              onClick={() => window.open('/cuisine', '_blank')}
              variant="outline"
              size="sm"
              className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="open-kitchen-view-btn"
              title="Vue cuisine plein écran"
            >
              🍳
              <span className="hidden sm:inline ml-1">Cuisine</span>
            </Button>
            {deliverySettings.orders_enabled ? (
              <Button
                onClick={() => { setPauseReason(''); setShowPauseModal(true); }}
                variant="outline"
                size="sm"
                className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="pause-orders-btn"
              >
                <Pause className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Pause commandes</span>
              </Button>
            ) : (
              <Button
                onClick={() => toggleOrdersEnabled(true)}
                variant="outline"
                size="sm"
                className="rounded-full border-green-300 text-green-700 hover:bg-green-50"
                data-testid="resume-orders-btn"
              >
                <Play className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Rouvrir</span>
              </Button>
            )}
            <Button onClick={playNotificationSound} variant="outline" size="sm" className="rounded-full hidden sm:flex" title="Tester le son">
              🔔
            </Button>
            <Button
              onClick={() => setSoundMuted(!soundMuted)}
              variant="outline"
              size="sm"
              className={`rounded-full ${soundMuted ? 'bg-red-50 border-red-300 text-red-700' : 'border-stone-300'}`}
              title={soundMuted ? 'Réactiver le son' : 'Couper le son'}
              data-testid="toggle-sound-btn"
            >
              {soundMuted ? '🔇' : '🔊'}
              <span className="hidden sm:inline ml-1">{soundMuted ? 'Muet' : 'Son'}</span>
            </Button>
            <Button onClick={fetchAllData} variant="outline" size="sm" className="rounded-full" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-full p-1 mb-6 flex flex-wrap gap-1">
            <TabsTrigger value="dashboard" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Tableau de bord</span>
              <span className="sm:hidden">Home</span>
            </TabsTrigger>
            <TabsTrigger value="commandes" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm" data-testid="tab-commandes">
              <ShoppingBag className="w-4 h-4 mr-1 sm:mr-2" />
              Commandes
              {pendingOrders.length > 0 && <Badge className={`ml-1 sm:ml-2 text-xs ${stalePendingCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'}`}>{pendingOrders.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="reservations" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm">
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Réservations</span>
              <span className="sm:hidden">Résa</span>
              {pendingReservations.length > 0 && <Badge className="ml-1 sm:ml-2 bg-yellow-500 text-xs">{pendingReservations.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="plats" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm">
              <UtensilsCrossed className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Plats</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm">
              <Settings className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Livraison</span>
            </TabsTrigger>
            <TabsTrigger value="stats" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm" data-testid="tab-stats">
              <TrendingUp className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Statistiques</span>
              <span className="sm:hidden">Stats+</span>
            </TabsTrigger>
            <TabsTrigger value="clients" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm" data-testid="tab-clients">
              <Users className="w-4 h-4 mr-1 sm:mr-2" />
              Clients
            </TabsTrigger>
            <TabsTrigger value="promo" className="rounded-full data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white text-xs sm:text-sm" data-testid="tab-promo">
              <Tag className="w-4 h-4 mr-1 sm:mr-2" />
              Codes promo
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#57534E] uppercase">Aujourd'hui</p>
                      <p className="text-2xl font-bold text-[#C62E2E]">{todayOrdersCount}</p>
                      <p className="text-xs text-[#57534E]">commande{todayOrdersCount > 1 ? 's' : ''}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <ShoppingBag className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#57534E] uppercase">7 derniers jours</p>
                      <p className="text-2xl font-bold text-[#1C1917]">{last7DaysOrders.length}</p>
                      <p className="text-xs text-[#57534E]">commande{last7DaysOrders.length > 1 ? 's' : ''}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#57534E] uppercase">En attente</p>
                      <p className="text-2xl font-bold text-yellow-600">{pendingOrders.length}</p>
                      <p className="text-xs text-[#57534E]">commandes</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#57534E] uppercase">Futures</p>
                      <p className="text-2xl font-bold text-purple-600">{futureOrders.length}</p>
                      <p className="text-xs text-[#57534E]">commandes</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <CalendarDays className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pending Orders */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-500" />
                      Commandes en attente
                    </CardTitle>
                    <Badge className="bg-yellow-100 text-yellow-800">{pendingOrders.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 max-h-80 overflow-y-auto">
                  {pendingOrders.length === 0 ? (
                    <p className="text-center text-[#57534E] py-4">Aucune commande en attente</p>
                  ) : (
                    pendingOrders.slice(0, 5).map((order) => (
                      <div key={order.id} className="bg-[#FAF9F6] rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{order.customer_name}</p>
                          <p className="text-xs text-[#57534E]">{getDateLabel(order.order_date)} • {order.desired_time || '⚡ Au plus vite'}</p>
                          <p className="text-sm font-bold text-[#C62E2E]">{order.total?.toFixed(2)} €</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleOrderStatus(order.id, 'confirmed')} className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0 rounded-full">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleOrderStatus(order.id, 'cancelled')} className="border-red-300 text-red-600 h-8 w-8 p-0 rounded-full">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Pending Reservations */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      Réservations en attente
                    </CardTitle>
                    <Badge className="bg-blue-100 text-blue-800">{pendingReservations.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 max-h-80 overflow-y-auto">
                  {pendingReservations.length === 0 ? (
                    <p className="text-center text-[#57534E] py-4">Aucune réservation en attente</p>
                  ) : (
                    pendingReservations.slice(0, 5).map((res) => (
                      <div key={res.id} className="bg-[#FAF9F6] rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm">{res.name}</p>
                          <p className="text-xs text-[#57534E]">{res.date} • {res.time}</p>
                          <p className="text-xs"><Users className="w-3 h-3 inline mr-1" />{res.guests || res.party_size} pers.</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleReservationStatus(res.id, 'confirmed')} className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0 rounded-full">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReservationStatus(res.id, 'cancelled')} className="border-red-300 text-red-600 h-8 w-8 p-0 rounded-full">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* COMMANDES TAB */}
          <TabsContent value="commandes">
            {/* Search + Filters row */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                <Input
                  type="search"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Rechercher par nom, téléphone, email, adresse, ID…"
                  className="pl-9 rounded-full"
                  data-testid="orders-search-input"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadExport('/admin/export/orders', 'commandes_notabene.csv')}
                className="rounded-full border-green-300 text-green-700 hover:bg-green-50"
                data-testid="export-orders-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Export Excel
              </Button>
            </div>

            {/* Filter buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button variant={orderFilter === 'pending' ? 'default' : 'outline'} size="sm" onClick={() => setOrderFilter('pending')} className={orderFilter === 'pending' ? 'bg-yellow-500' : ''} data-testid="filter-pending">
                <Clock className="w-4 h-4 mr-1" /> En attente ({pendingOrders.length})
                {stalePendingCount > 0 && <span className="ml-1 text-red-200 text-xs">⚠ {stalePendingCount}</span>}
              </Button>
              <Button variant={orderFilter === 'confirmed' ? 'default' : 'outline'} size="sm" onClick={() => setOrderFilter('confirmed')} className={orderFilter === 'confirmed' ? 'bg-blue-500' : ''} data-testid="filter-confirmed">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmées ({confirmedOrders.length})
              </Button>
              <Button variant={orderFilter === 'today' ? 'default' : 'outline'} size="sm" onClick={() => setOrderFilter('today')} className={orderFilter === 'today' ? 'bg-green-500' : ''} data-testid="filter-today">
                <CalendarDays className="w-4 h-4 mr-1" /> Aujourd'hui ({todayOrders.length})
              </Button>
              <Button variant={orderFilter === 'future' ? 'default' : 'outline'} size="sm" onClick={() => setOrderFilter('future')} className={orderFilter === 'future' ? 'bg-purple-500' : ''} data-testid="filter-future">
                <Calendar className="w-4 h-4 mr-1" /> Futures ({futureOrders.length})
              </Button>
              <Button variant={orderFilter === 'completed' ? 'default' : 'outline'} size="sm" onClick={() => setOrderFilter('completed')} className={orderFilter === 'completed' ? 'bg-gray-500' : ''} data-testid="filter-completed">
                <History className="w-4 h-4 mr-1" /> Terminées ({completedOrders.length})
              </Button>
            </div>

            {/* Orders List */}
            {getFilteredOrders().length === 0 ? (
              <Card className="p-12 text-center">
                <ShoppingBag className="w-12 h-12 text-[#57534E] mx-auto mb-4 opacity-50" />
                <p className="text-[#57534E]">
                  {orderSearch ? `Aucune commande ne correspond à "${orderSearch}"` : 'Aucune commande'}
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {groupByDate(getFilteredOrders(), 'order_date').map((group) => (
                  <div key={group.key} data-testid={`orders-day-${group.key}`}>
                    <div className="flex items-center gap-3 mb-3 sticky top-0 bg-[#F5F2EA] z-10 py-2">
                      <CalendarDays className="w-5 h-5 text-[#C62E2E]" />
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1C1917] capitalize">
                        {group.label}
                      </h3>
                      <Badge className="bg-[#C62E2E]/10 text-[#C62E2E]">
                        {group.items.length} commande{group.items.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {group.items.map((order) => {
                        const minutesElapsed = getMinutesSinceCreated(order);
                        const isStale = order.status === 'pending' && minutesElapsed > 15;
                        return (
                  <Card key={order.id} className={`p-4 ${isStale ? 'border-2 border-red-400 bg-red-50/30' : ''}`}>
                    <div className="flex flex-col lg:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="font-mono text-xs bg-[#FAF9F6] px-2 py-1 rounded">#{order.id?.slice(-6).toUpperCase()}</span>
                          {getStatusBadge(order.status)}
                          {order.status === 'pending' && (
                            <Badge className={isStale ? 'bg-red-500 text-white' : 'bg-stone-100 text-stone-700'}>
                              <Timer className="w-3 h-3 mr-1" />
                              {getRelativeTimeLabel(order)}
                            </Badge>
                          )}
                          <Badge className={order.order_type === 'livraison' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                            {order.order_type === 'livraison' ? <Truck className="w-3 h-3 mr-1" /> : <Package className="w-3 h-3 mr-1" />}
                            {order.order_type === 'livraison' ? 'Livraison' : 'Emporter'}
                          </Badge>
                          <Badge className={order.payment_method === 'cash' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>
                            {order.payment_method === 'cash' ? <Banknote className="w-3 h-3 mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />}
                            {order.payment_method === 'cash' ? 'Espèces' : 'Carte'}
                          </Badge>
                          {isScheduledOrder(order) ? (
                            <Badge className="bg-purple-100 text-purple-800 border border-purple-300" data-testid="scheduled-badge">
                              🗓️ Planifiée
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="immediate-badge">
                              ⚡ Aujourd'hui
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                          <p><User className="w-4 h-4 inline mr-1" /> {order.customer_name}</p>
                          <p><Phone className="w-4 h-4 inline mr-1" /> {order.customer_phone}</p>
                          <p><CalendarDays className="w-4 h-4 inline mr-1" /> {getDateLabel(order.order_date)}</p>
                          <p><Clock className="w-4 h-4 inline mr-1" /> {order.desired_time || '⚡ Au plus vite'}</p>
                        </div>
                        {(() => {
                          if (!order.created_at) return null;
                          let arrivalLabel = '';
                          try {
                            const created = parseISO(order.created_at);
                            const hh = String(created.getHours()).padStart(2, '0');
                            const mm = String(created.getMinutes()).padStart(2, '0');
                            arrivalLabel = `${hh}h${mm}`;
                          } catch { return null; }
                          return (
                            <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-md border bg-stone-50 border-stone-300 text-stone-900" data-testid="arrival-time-badge">
                              <Clock className="w-4 h-4" />
                              <span className="font-semibold">Commande reçue à {arrivalLabel}</span>
                              <span className="text-xs opacity-75">({getRelativeTimeLabel(order)})</span>
                            </div>
                          );
                        })()}
                        {order.delivery_address && (
                          <p className="text-sm text-[#57534E]">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            {order.delivery_postal_code && (
                              <span className="font-bold text-[#C62E2E] mr-1">{order.delivery_postal_code}</span>
                            )}
                            {order.delivery_address}
                          </p>
                        )}
                        <div className="mt-2 text-sm space-y-1">
                          {order.items?.map((item, idx) => (
                            <div key={idx}>
                              <span className="font-medium">{item.quantity}x {item.name}</span>
                              {item.pasta_type && <span className="text-[#F59E0B] ml-1">({item.pasta_type})</span>}
                              {item.is_giant && <span className="ml-1 inline-block bg-[#C62E2E] text-white text-[10px] px-1.5 py-0.5 rounded">GÉANTE</span>}
                              {(item.supplements || []).length > 0 && (
                                <ul className="ml-5 mt-0.5 text-xs text-[#57534E]">
                                  {item.supplements.map((s, sidx) => (
                                    <li key={sidx}>
                                      <span className="text-[#3A6351]">+</span> {s.name}
                                      <span className="text-[#3A6351] ml-1">
                                        {s.price === 0
                                          ? '(Gratuit)'
                                          : `(+${s.price.toFixed(2).replace('.', ',')} €)`}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                        {order.notes && (
                          <p className="text-sm text-[#57534E] mt-2 italic bg-yellow-50 px-2 py-1 rounded">
                            📝 {order.notes}
                          </p>
                        )}
                        <p className="font-bold text-lg text-[#C62E2E] mt-2">{order.total?.toFixed(2)} €</p>
                      </div>
                      <div className="flex flex-row lg:flex-col gap-2 justify-end">
                        {order.status === 'pending' && (
                          <>
                            <Button size="sm" onClick={() => handleOrderStatus(order.id, 'confirmed')} className="bg-green-600 hover:bg-green-700" data-testid="confirm-order-btn">
                              <Check className="w-4 h-4 mr-1" /> Confirmer
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleOrderStatus(order.id, 'cancelled')} className="border-red-300 text-red-600" data-testid="refuse-order-btn">
                              <X className="w-4 h-4 mr-1" /> Refuser
                            </Button>
                          </>
                        )}
                        {order.status === 'confirmed' && (
                          <>
                            <Button size="sm" onClick={() => handleOrderStatus(order.id, 'completed')} className="bg-blue-600 hover:bg-blue-700" data-testid="complete-order-btn">
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Terminer
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openUpdateDelayModal(order)} className="border-amber-300 text-amber-700" data-testid="update-delay-btn">
                              <Clock className="w-4 h-4 mr-1" /> Modifier délai
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleOrderStatus(order.id, 'cancelled')} className="border-red-300 text-red-600" data-testid="cancel-confirmed-order-btn">
                              <X className="w-4 h-4 mr-1" /> Annuler
                            </Button>
                          </>
                        )}
                        {order.status === 'completed' && (
                          <Button size="sm" variant="outline" onClick={() => handleOrderStatus(order.id, 'cancelled')} className="border-red-300 text-red-600" data-testid="cancel-completed-order-btn">
                            <X className="w-4 h-4 mr-1" /> Annuler
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printOrderTicket(order)}
                          className="border-stone-300 text-[#1C1917] hover:bg-stone-50"
                          title="Imprimer le ticket (80mm)"
                          data-testid="print-order-btn"
                        >
                          <Printer className="w-4 h-4 mr-1" /> Imprimer
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteOrder(order.id)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-700"
                          title="Supprimer définitivement"
                          data-testid="delete-order-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                        </Button>
                      </div>
                    </div>
                  </Card>
                  );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* RESERVATIONS TAB */}
          <TabsContent value="reservations">
            {(() => {
              const filterFn = (r) => {
                if (reservationFilter === 'all') return true;
                if (reservationFilter === 'upcoming') return r.status === 'pending' || r.status === 'confirmed';
                return r.status === reservationFilter;
              };
              const filtered = reservations.filter(filterFn);
              const counts = {
                upcoming: reservations.filter(r => r.status === 'pending' || r.status === 'confirmed').length,
                pending: reservations.filter(r => r.status === 'pending').length,
                confirmed: reservations.filter(r => r.status === 'confirmed').length,
                completed: reservations.filter(r => r.status === 'completed').length,
                cancelled: reservations.filter(r => r.status === 'cancelled').length,
                all: reservations.length,
              };
              return (
                <>
                  {/* Reservation blocks management */}
                  <Card className="p-4 mb-4 border-amber-200 bg-amber-50/50" data-testid="reservation-blocks-card">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-amber-700" />
                      <h3 className="font-semibold text-[#1C1917]">Bloquer les réservations sur un créneau</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 items-end mb-3">
                      <div className="flex-1 min-w-[140px]">
                        <Label className="text-xs">Date de début</Label>
                        <Input
                          type="date"
                          value={newBlock.date}
                          min={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
                          data-testid="block-date-input"
                        />
                      </div>
                      <div className="flex-1 min-w-[140px]">
                        <Label className="text-xs">Date de fin <span className="text-[#57534E] font-normal">(optionnel)</span></Label>
                        <Input
                          type="date"
                          value={newBlock.end_date}
                          min={newBlock.date || new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setNewBlock({ ...newBlock, end_date: e.target.value })}
                          data-testid="block-end-date-input"
                        />
                      </div>
                      <div className="min-w-[140px]">
                        <Label className="text-xs">Période</Label>
                        <Select
                          value={newBlock.period}
                          onValueChange={(v) => setNewBlock({ ...newBlock, period: v })}
                        >
                          <SelectTrigger data-testid="block-period-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lunch">🍝 Midi (12h-13h30)</SelectItem>
                            <SelectItem value="dinner">🍕 Soir (18h-21h)</SelectItem>
                            <SelectItem value="all_day">🚫 Journée entière</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <Label className="text-xs">Raison (optionnel — visible client)</Label>
                        <Input
                          placeholder="Ex: Privatisation, Congés..."
                          value={newBlock.reason}
                          onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                          data-testid="block-reason-input"
                        />
                      </div>
                      <Button
                        onClick={handleAddReservationBlock}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="add-block-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Bloquer
                      </Button>
                    </div>
                    <p className="text-xs text-[#57534E] mb-2">
                      💡 Laisse la date de fin vide pour bloquer un seul jour. Sinon, tous les jours de l'intervalle seront bloqués pour la période choisie.
                    </p>
                    {reservationBlocks.length > 0 ? (
                      <div className="space-y-1 mt-2">
                        {reservationBlocks.map((b) => {
                          const periodLabel = b.period === 'lunch' ? 'Midi' : b.period === 'dinner' ? 'Soir' : 'Journée entière';
                          return (
                            <div key={b.id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-amber-200 text-sm" data-testid={`block-row-${b.id}`}>
                              <div className="flex-1">
                                <span className="font-semibold">{format(parseISO(b.date), 'EEEE d MMMM yyyy', { locale: fr })}</span>
                                <Badge className="ml-2 bg-amber-100 text-amber-800 border border-amber-300">{periodLabel}</Badge>
                                {b.reason && <span className="text-[#57534E] ml-2 italic">— {b.reason}</span>}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteReservationBlock(b.id)}
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-block-${b.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-[#57534E] italic">Aucun créneau bloqué actuellement.</p>
                    )}
                  </Card>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {[
                      { key: 'upcoming', label: 'À venir' },
                      { key: 'pending', label: 'En attente' },
                      { key: 'confirmed', label: 'Confirmées' },
                      { key: 'completed', label: 'Terminées' },
                      { key: 'cancelled', label: 'Annulées' },
                      { key: 'all', label: 'Toutes' },
                    ].map(({ key, label }) => (
                      <Button
                        key={key}
                        size="sm"
                        variant={reservationFilter === key ? 'default' : 'outline'}
                        onClick={() => setReservationFilter(key)}
                        className={reservationFilter === key ? 'bg-[#C62E2E] hover:bg-[#a02424]' : ''}
                        data-testid={`reservation-filter-${key}`}
                      >
                        {label}
                        <Badge className="ml-2 bg-white/20 text-current">{counts[key]}</Badge>
                      </Button>
                    ))}
                  </div>
                  {filtered.length === 0 ? (
                    <Card className="p-12 text-center">
                      <Calendar className="w-12 h-12 text-[#57534E] mx-auto mb-4 opacity-50" />
                      <p className="text-[#57534E]">
                        Aucune réservation {reservationFilter === 'upcoming' ? 'à venir' : reservationFilter === 'all' ? '' : reservationFilter === 'pending' ? 'en attente' : reservationFilter === 'confirmed' ? 'confirmée' : reservationFilter === 'completed' ? 'terminée' : 'annulée'}
                      </p>
                    </Card>
                  ) : (
                    <div className="space-y-6">
                      {groupByDate(filtered, 'date').map((group) => (
                        <div key={group.key} data-testid={`reservations-day-${group.key}`}>
                          <div className="flex items-center gap-3 mb-3 sticky top-0 bg-[#F5F2EA] z-10 py-2">
                            <CalendarDays className="w-5 h-5 text-[#C62E2E]" />
                            <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1C1917] capitalize">
                              {group.label}
                            </h3>
                            <Badge className="bg-[#C62E2E]/10 text-[#C62E2E]">
                              {group.items.length} réservation{group.items.length > 1 ? 's' : ''}
                            </Badge>
                          </div>
                          <div className="space-y-3">
                            {group.items.map((res) => (
                              <Card key={res.id} className="p-4">
                                <div className="flex flex-col lg:flex-row justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      {getStatusBadge(res.status)}
                                      <Badge className="bg-[#3A6351] text-white"><Users className="w-3 h-3 mr-1" /> {res.guests || res.party_size} personnes</Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <p><User className="w-4 h-4 inline mr-1" /> {res.name}</p>
                                      <p><Phone className="w-4 h-4 inline mr-1" /> {res.phone}</p>
                                      <p className="font-semibold"><CalendarDays className="w-4 h-4 inline mr-1 text-[#C62E2E]" /> {res.date}</p>
                                      <p className="font-semibold"><Clock className="w-4 h-4 inline mr-1 text-[#C62E2E]" /> {res.time}</p>
                                    </div>
                                    {res.email && <p className="text-sm text-[#57534E] mt-1"><Mail className="w-4 h-4 inline mr-1" /> {res.email}</p>}
                                    {res.notes && <p className="text-sm text-[#57534E] mt-1 italic">📝 {res.notes}</p>}
                                    {res.cancel_reason && <p className="text-sm text-red-700 mt-1 bg-red-50 px-2 py-1 rounded">❌ Motif : {res.cancel_reason}</p>}
                                  </div>
                                  <div className="flex flex-row lg:flex-col gap-2 justify-end">
                                    {res.status === 'pending' && (
                                      <>
                                        <Button size="sm" onClick={() => handleReservationStatus(res.id, 'confirmed')} className="bg-green-600 hover:bg-green-700">
                                          <Check className="w-4 h-4 mr-1" /> Confirmer
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => handleReservationStatus(res.id, 'cancelled')} className="border-red-300 text-red-600">
                                          <X className="w-4 h-4 mr-1" /> Refuser
                                        </Button>
                                      </>
                                    )}
                                    {res.status === 'confirmed' && (
                                      <>
                                        <Button size="sm" onClick={() => handleReservationStatus(res.id, 'completed')} className="bg-blue-600 hover:bg-blue-700" data-testid="complete-reservation-btn">
                                          <CheckCircle2 className="w-4 h-4 mr-1" /> Terminer
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => handleReservationStatus(res.id, 'cancelled')} className="border-red-300 text-red-600">
                                          <X className="w-4 h-4 mr-1" /> Annuler
                                        </Button>
                                      </>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteReservation(res.id)}
                                      className="text-red-500 hover:bg-red-50 hover:text-red-700"
                                      title="Supprimer définitivement"
                                      data-testid="delete-reservation-btn"
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                                    </Button>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>

          {/* PLATS TAB */}
          <TabsContent value="plats">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-64 space-y-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Catégories</h3>
                  <Button size="sm" variant="outline" onClick={() => setShowCategoryModal(true)} className="rounded-full">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {categories.map(cat => (
                  <div key={cat.id} className="relative group">
                    <button
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-2 ${selectedCategory === cat.id ? 'bg-[#C62E2E] text-white' : 'bg-white hover:bg-stone-100'}`}
                    >
                      <span>{cat.icon}</span>
                      <span className="flex-1 text-sm">{cat.label}</span>
                      <span className="text-xs opacity-70">({menuItems.filter(i => i.category === cat.id).length})</span>
                    </button>
                    {cat.is_custom && (
                      <button onClick={() => handleDeleteCategory(cat.id)} className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">{categories.find(c => c.id === selectedCategory)?.label || 'Plats'}</h3>
                  <Button onClick={() => { setNewItem({...newItem, category: selectedCategory}); setShowAddItemModal(true); }} className="bg-[#C62E2E] hover:bg-[#a02424] rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {filteredMenuItems.map(item => (
                    <Card key={item.id} className={`p-3 ${!item.is_available ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{item.name}</span>
                            {item.is_custom && <Badge variant="outline" className="text-xs">Perso</Badge>}
                            {!item.is_available && <Badge className="bg-red-100 text-red-800 text-xs">Indispo</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-[#C62E2E] text-sm">{item.price?.toFixed(2)} €</span>
                          <Switch checked={item.is_available} onCheckedChange={() => toggleItemAvailability(item.id)} />
                          <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setShowEditItemModal(true); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteItem(item.id, item.is_custom)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="font-semibold text-lg mb-4">Paramètres de livraison</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Délai estimé (minutes)</Label>
                    <div className="grid grid-cols-2 gap-3 mt-1">
                      <div>
                        <p className="text-xs text-[#78716C] mb-1">Min</p>
                        <Input
                          type="number"
                          value={deliverySettings.delivery_time_minutes ?? ''}
                          onChange={(e) => setDeliverySettings({ ...deliverySettings, delivery_time_minutes: parseInt(e.target.value) || 0 })}
                          min="10"
                          max="180"
                          data-testid="dashboard-delivery-time-min"
                        />
                      </div>
                      <div>
                        <p className="text-xs text-[#78716C] mb-1">Max <span className="text-[#A8A29E]">(optionnel)</span></p>
                        <Input
                          type="number"
                          value={deliverySettings.delivery_time_max_minutes ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDeliverySettings({ ...deliverySettings, delivery_time_max_minutes: v === '' ? null : parseInt(v) || 0 });
                          }}
                          min="10"
                          max="180"
                          placeholder="Ex: 75"
                          data-testid="dashboard-delivery-time-max"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-[#57534E] mt-2">
                      {deliverySettings.delivery_time_max_minutes
                        ? <>Aperçu client : <strong>{deliverySettings.delivery_time_minutes}-{deliverySettings.delivery_time_max_minutes} min</strong></>
                        : <>Aperçu client : <strong>{deliverySettings.delivery_time_minutes} min</strong> (vide le Max pour repasser en valeur unique)</>
                      }
                    </p>
                  </div>
                  <div>
                    <Label>Commande minimum (€)</Label>
                    <Input type="number" value={deliverySettings.min_order_amount} onChange={(e) => setDeliverySettings({...deliverySettings, min_order_amount: parseFloat(e.target.value)})} className="mt-1" />
                  </div>
                  <div className="flex items-center gap-4">
                    <Switch checked={deliverySettings.delivery_enabled} onCheckedChange={(checked) => setDeliverySettings({...deliverySettings, delivery_enabled: checked})} />
                    <Label>Livraison activée</Label>
                  </div>
                  <Button onClick={saveDeliverySettings} className="bg-[#C62E2E] hover:bg-[#a02424] rounded-full">Enregistrer</Button>
                </div>
              </Card>
              <Card className="p-6">
                <h3 className="font-semibold text-lg mb-4">Codes postaux autorisés</h3>
                <div className="flex gap-2 mb-4">
                  <Input placeholder="Code postal" value={newPostalCode} onChange={(e) => setNewPostalCode(e.target.value)} maxLength={4} className="w-24" />
                  <Button onClick={() => { if (newPostalCode && !postalCodes.includes(newPostalCode)) { const newCodes = [...postalCodes, newPostalCode]; setPostalCodes(newCodes); savePostalCodes(newCodes); setNewPostalCode(''); } }} variant="outline" className="rounded-full">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {postalCodes.map(code => (
                    <Badge key={code} className="bg-[#FAF9F6] text-[#1C1917] hover:bg-red-100 cursor-pointer" onClick={() => { const newCodes = postalCodes.filter(c => c !== code); setPostalCodes(newCodes); savePostalCodes(newCodes); }}>
                      {code} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* STATS TAB */}
          <TabsContent value="stats" data-testid="stats-tab-content">
            {!stats ? (
              <Card className="p-12 text-center">
                <TrendingUp className="w-12 h-12 text-[#57534E] mx-auto mb-4 opacity-50" />
                <p className="text-[#57534E]">Chargement des statistiques...</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: "Aujourd'hui", data: stats.today, color: '#C62E2E', bg: 'bg-red-50' },
                    { label: '7 derniers jours', data: stats.week, color: '#2563eb', bg: 'bg-blue-50' },
                    { label: '30 derniers jours', data: stats.month, color: '#16a34a', bg: 'bg-green-50' },
                    { label: 'Depuis le début', data: stats.all_time, color: '#7c3aed', bg: 'bg-purple-50' },
                  ].map((k) => (
                    <Card key={k.label} className={k.bg}>
                      <CardContent className="p-5">
                        <p className="text-xs text-[#57534E] uppercase mb-2">{k.label}</p>
                        <p className="text-3xl font-bold" style={{ color: k.color }}>{(k.data?.revenue ?? 0).toFixed(2)} €</p>
                        <div className="mt-2 text-sm text-[#57534E] space-y-0.5">
                          <p>{k.data?.orders ?? 0} commandes</p>
                          <p>Panier moyen : <strong>{(k.data?.avg_basket ?? 0).toFixed(2)} €</strong></p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Monthly breakdown */}
                <Card data-testid="monthly-breakdown-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CalendarDays className="w-5 h-5 text-[#C62E2E]" />
                      Détail par mois
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(!stats.monthly_series || stats.monthly_series.length === 0) ? (
                      <p className="text-[#57534E] text-sm">Aucune donnée mensuelle disponible.</p>
                    ) : (() => {
                      const series = [...stats.monthly_series].reverse(); // most recent first
                      const maxRev = Math.max(1, ...series.map(m => m.revenue));
                      const monthLabel = (key) => {
                        try {
                          const [y, m] = key.split('-');
                          const d = new Date(parseInt(y), parseInt(m) - 1, 1);
                          return format(d, 'MMMM yyyy', { locale: fr });
                        } catch { return key; }
                      };
                      return (
                        <div className="space-y-2">
                          {series.map((m) => (
                            <div key={m.month} className="grid grid-cols-12 gap-3 items-center text-sm py-2 border-b border-stone-100 last:border-0" data-testid={`month-row-${m.month}`}>
                              <div className="col-span-12 sm:col-span-3 font-semibold capitalize">{monthLabel(m.month)}</div>
                              <div className="col-span-6 sm:col-span-5">
                                <div className="bg-stone-100 rounded-full h-3 relative">
                                  <div
                                    className="bg-[#C62E2E] h-3 rounded-full transition-all"
                                    style={{ width: `${(m.revenue / maxRev) * 100}%` }}
                                  />
                                </div>
                              </div>
                              <div className="col-span-3 sm:col-span-2 text-right font-bold text-[#C62E2E]">
                                {m.revenue.toFixed(2)} €
                              </div>
                              <div className="col-span-3 sm:col-span-2 text-right text-[#57534E]">
                                {m.orders} cmd • {(m.avg_basket ?? 0).toFixed(2)} €
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* 30-day CA chart */}
                <Card>
                  <CardHeader><CardTitle className="text-lg">CA sur 30 jours</CardTitle></CardHeader>
                  <CardContent>
                    {(() => {
                      const series = stats.daily_series;
                      const max = Math.max(1, ...series.map(d => d.revenue));
                      return (
                        <div className="flex items-end gap-1 h-48 overflow-x-auto" data-testid="revenue-chart">
                          {series.map((d) => (
                            <div key={d.date} className="flex-1 min-w-[12px] h-full flex flex-col justify-end group cursor-default relative">
                              <div
                                className="w-full bg-[#C62E2E] rounded-t transition-all hover:bg-[#a02424] relative"
                                style={{ height: `${Math.max(2, (d.revenue / max) * 100)}%` }}
                                title={`${d.date} : ${d.revenue.toFixed(2)} € • ${d.orders} commandes`}
                              >
                                <span className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-[#1C1917] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                                  {d.revenue.toFixed(0)} €
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="flex justify-between text-xs text-[#57534E] mt-2">
                      <span>il y a 30 jours</span>
                      <span>aujourd'hui</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Two columns: Top dishes + Peak hours */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle className="text-lg">Top 10 plats (tout temps)</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {stats.top_dishes.length === 0 ? (
                        <p className="text-[#57534E] text-sm">Aucune donnée</p>
                      ) : stats.top_dishes.map((d, i) => (
                        <div key={d.name} className="flex items-center justify-between gap-3 text-sm">
                          <span className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="w-6 text-[#57534E] font-mono">{i + 1}.</span>
                            <span className="truncate">{d.name}</span>
                          </span>
                          <span className="text-[#57534E]">{d.count}×</span>
                          <span className="font-bold text-[#C62E2E] w-20 text-right">{d.revenue.toFixed(2)} €</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle className="text-lg">Heures de pointe</CardTitle></CardHeader>
                    <CardContent>
                      {stats.peak_hours.length === 0 ? (
                        <p className="text-[#57534E] text-sm">Aucune donnée</p>
                      ) : (() => {
                        const maxH = Math.max(1, ...stats.peak_hours.map(h => h.count));
                        return (
                          <div className="space-y-1.5">
                            {stats.peak_hours.map((h) => (
                              <div key={h.hour} className="flex items-center gap-2 text-sm">
                                <span className="w-12 text-[#57534E] font-mono">{String(h.hour).padStart(2, '0')}h</span>
                                <div className="flex-1 bg-stone-100 rounded-full h-4 relative">
                                  <div className="bg-[#C62E2E] h-4 rounded-full" style={{ width: `${(h.count / maxH) * 100}%` }} />
                                </div>
                                <span className="w-10 text-right font-semibold">{h.count}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Distribution cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle className="text-lg">Type de commande</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <Package className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                          <p className="text-2xl font-bold text-purple-700">{stats.type_distribution.emporter || 0}</p>
                          <p className="text-xs text-[#57534E]">À emporter</p>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <Truck className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                          <p className="text-2xl font-bold text-blue-700">{stats.type_distribution.livraison || 0}</p>
                          <p className="text-xs text-[#57534E]">Livraison</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-lg">Méthode de paiement</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <Banknote className="w-6 h-6 text-orange-600 mx-auto mb-1" />
                          <p className="text-2xl font-bold text-orange-700">{stats.payment_distribution.cash || 0}</p>
                          <p className="text-xs text-[#57534E]">Espèces</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <CreditCard className="w-6 h-6 text-green-600 mx-auto mb-1" />
                          <p className="text-2xl font-bold text-green-700">{stats.payment_distribution.card || 0}</p>
                          <p className="text-xs text-[#57534E]">Carte</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* CLIENTS TAB */}
          <TabsContent value="clients" data-testid="clients-tab-content">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                <Input
                  type="search"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Rechercher un client (nom, téléphone, email)…"
                  className="pl-9 rounded-full"
                  data-testid="customers-search-input"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadExport('/admin/export/customers', 'clients_notabene.csv')}
                className="rounded-full border-green-300 text-green-700 hover:bg-green-50"
                data-testid="export-customers-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Export Excel
              </Button>
            </div>

            {filteredCustomers.length === 0 ? (
              <Card className="p-12 text-center">
                <Users className="w-12 h-12 text-[#57534E] mx-auto mb-4 opacity-50" />
                <p className="text-[#57534E]">
                  {customerSearch ? 'Aucun client ne correspond à la recherche' : 'Aucun client pour le moment'}
                </p>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAF9F6] border-b">
                      <tr>
                        <th className="text-left p-3 font-semibold">Client</th>
                        <th className="text-left p-3 font-semibold">Contact</th>
                        <th className="text-center p-3 font-semibold">Commandes</th>
                        <th className="text-center p-3 font-semibold">Réservations</th>
                        <th className="text-right p-3 font-semibold">Total dépensé</th>
                        <th className="text-left p-3 font-semibold hidden md:table-cell">Dernière activité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.map((c) => (
                        <tr key={c.phone} className="border-b hover:bg-stone-50">
                          <td className="p-3">
                            <div className="font-medium text-[#1C1917]">{c.name || '—'}</div>
                            {c.postal_codes.length > 0 && (
                              <div className="text-xs text-[#57534E]">
                                CP : {c.postal_codes.join(', ')}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="text-[#57534E]">{c.phone}</div>
                            {c.email && <div className="text-xs text-[#57534E]">{c.email}</div>}
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={c.order_count >= 5 ? 'bg-[#C62E2E] text-white' : c.order_count > 0 ? 'bg-[#1C1917] text-white' : 'bg-stone-200 text-stone-500'}>
                              {c.order_count} {c.order_count >= 5 ? '⭐' : ''}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={(c.reservation_count || 0) >= 3 ? 'bg-purple-700 text-white' : (c.reservation_count || 0) > 0 ? 'bg-purple-600 text-white' : 'bg-stone-200 text-stone-500'}>
                              {c.reservation_count || 0}
                            </Badge>
                          </td>
                          <td className="p-3 text-right font-bold text-[#C62E2E]">{c.total_spent.toFixed(2)} €</td>
                          <td className="p-3 text-[#57534E] text-xs hidden md:table-cell">
                            {(c.last_activity || c.last_order) ? format(parseISO(c.last_activity || c.last_order), 'd MMM yyyy', { locale: fr }) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* PROMO CODES TAB */}
          <TabsContent value="promo">
            <AdminPromoCodes />
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <Dialog open={showAddItemModal} onOpenChange={setShowAddItemModal}>
          <DialogContent>
            <DialogHeader><DialogTitle>Ajouter un plat</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nom *</Label><Input value={newItem.name} onChange={(e) => setNewItem({...newItem, name: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={newItem.description} onChange={(e) => setNewItem({...newItem, description: e.target.value})} /></div>
              <div><Label>Prix (€) *</Label><Input type="number" step="0.5" value={newItem.price} onChange={(e) => setNewItem({...newItem, price: e.target.value})} /></div>
              <div><Label>Catégorie *</Label><Select value={newItem.category} onValueChange={(v) => setNewItem({...newItem, category: v})}><SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowAddItemModal(false)}>Annuler</Button><Button onClick={handleAddItem} className="bg-[#C62E2E]">Ajouter</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={showEditItemModal} onOpenChange={setShowEditItemModal}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier le plat</DialogTitle></DialogHeader>
            {editingItem && (<div className="space-y-4"><div><Label>Nom</Label><Input value={editingItem.name} onChange={(e) => setEditingItem({...editingItem, name: e.target.value})} /></div><div><Label>Description</Label><Textarea value={editingItem.description || ''} onChange={(e) => setEditingItem({...editingItem, description: e.target.value})} /></div><div><Label>Prix (€)</Label><Input type="number" step="0.5" value={editingItem.price} onChange={(e) => setEditingItem({...editingItem, price: e.target.value})} /></div></div>)}
            <DialogFooter><Button variant="outline" onClick={() => setShowEditItemModal(false)}>Annuler</Button><Button onClick={handleEditItem} className="bg-[#C62E2E]">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle catégorie</DialogTitle></DialogHeader>
            <div className="space-y-4"><div><Label>Nom *</Label><Input value={newCategory.label} onChange={(e) => setNewCategory({...newCategory, label: e.target.value})} placeholder="Ex: Desserts" /></div><div><Label>Emoji</Label><Input value={newCategory.icon} onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})} placeholder="🍰" /></div></div>
            <DialogFooter><Button variant="outline" onClick={() => setShowCategoryModal(false)}>Annuler</Button><Button onClick={handleAddCategory} className="bg-[#C62E2E]">Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Reason Modal */}
        <Dialog open={showCancelModal} onOpenChange={(open) => { if (!open) { setShowCancelModal(false); setCancelTarget(null); } }}>
          <DialogContent data-testid="cancel-reason-modal">
            <DialogHeader>
              <DialogTitle>
                {cancelTarget?.type === 'reservation' ? 'Refuser la réservation' : 'Refuser la commande'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[#57534E]">
                Sélectionnez un motif. Le client en sera informé par email.
              </p>
              <div className="space-y-2">
                {CANCEL_REASONS.map((reason) => (
                  <label
                    key={reason}
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      cancelReason === reason
                        ? 'border-[#C62E2E] bg-red-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`cancel-reason-option-${reason}`}
                  >
                    <input
                      type="radio"
                      name="cancel-reason"
                      value={reason}
                      checked={cancelReason === reason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      className="accent-[#C62E2E]"
                    />
                    <span className="text-sm">{reason}</span>
                  </label>
                ))}
              </div>
              {cancelReason === 'Autre (préciser)' && (
                <div>
                  <Label>Précisez le motif *</Label>
                  <Textarea
                    value={customCancelReason}
                    onChange={(e) => setCustomCancelReason(e.target.value)}
                    placeholder="Expliquez au client pourquoi sa commande est annulée…"
                    className="mt-1"
                    data-testid="cancel-reason-custom-input"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setShowCancelModal(false); setCancelTarget(null); }}
                data-testid="cancel-reason-back-btn"
              >
                Retour
              </Button>
              <Button
                onClick={submitCancellation}
                className="bg-[#C62E2E] hover:bg-[#a02424]"
                disabled={!cancelReason || (cancelReason === 'Autre (préciser)' && !customCancelReason.trim())}
                data-testid="cancel-reason-confirm-btn"
              >
                Confirmer le refus
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pause Orders Modal */}
        <Dialog open={showPauseModal} onOpenChange={setShowPauseModal}>
          <DialogContent data-testid="pause-orders-modal">
            <DialogHeader>
              <DialogTitle>Mettre en pause les commandes en ligne</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  Les clients verront un message à la place du bouton « Commander ». Les réservations de table restent actives.
                </p>
              </div>
              <div>
                <Label>Message affiché aux clients (optionnel)</Label>
                <Textarea
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder="Ex : Trop de commandes, réouverture dans 30 minutes."
                  className="mt-1"
                  data-testid="pause-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPauseModal(false)}>Annuler</Button>
              <Button
                onClick={() => toggleOrdersEnabled(false, pauseReason)}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="pause-orders-confirm-btn"
              >
                <Pause className="w-4 h-4 mr-1" /> Mettre en pause
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Order with delivery delay Modal */}
        <Dialog open={showConfirmModal} onOpenChange={(open) => { if (!open) { setShowConfirmModal(false); setConfirmTarget(null); setIsDelayUpdate(false); } }}>
          <DialogContent data-testid="confirm-order-modal">
            {(() => {
              const scheduled = isScheduledOrder(confirmTarget);
              const desiredTimePicked = hasDesiredTime(confirmTarget);
              const useDesiredTimeFlow = scheduled || desiredTimePicked;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle>
                      {isDelayUpdate
                        ? 'Modifier le délai estimé'
                        : scheduled
                          ? '🗓️ Confirmer la commande planifiée'
                          : desiredTimePicked
                            ? `🕐 Confirmer pour ${confirmTarget?.desired_time}`
                            : confirmTarget?.order_type === 'livraison'
                              ? '⚡ Confirmer la livraison (au plus vite)'
                              : '⚡ Confirmer la commande à emporter (au plus vite)'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {useDesiredTimeFlow && !isDelayUpdate ? (
                      // ===== CLIENT PICKED A SPECIFIC TIME (today OR scheduled) =====
                      <>
                        <div className={`border-2 rounded-xl p-4 ${scheduled ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
                          <div className="flex items-start gap-3">
                            <CalendarDays className={`w-5 h-5 shrink-0 mt-0.5 ${scheduled ? 'text-purple-700' : 'text-blue-700'}`} />
                            <div className="text-sm">
                              <p className={`font-semibold mb-1 ${scheduled ? 'text-purple-900' : 'text-blue-900'}`}>
                                {scheduled ? 'Commande planifiée' : 'Heure choisie par le client'}
                              </p>
                              <p className={scheduled ? 'text-purple-900' : 'text-blue-900'}>
                                Le client souhaite&nbsp;:&nbsp;
                                <strong>{getDateLabel(confirmTarget?.order_date)}</strong>
                                {confirmTarget?.desired_time && <> à <strong>{confirmTarget.desired_time}</strong></>}
                              </p>
                              <p className={`mt-2 text-xs ${scheduled ? 'text-purple-800' : 'text-blue-800'}`}>
                                ℹ️ L'email confirmera <strong>cette heure</strong>. Si tu confirmes sans saisir de délai, aucun "dans X minutes" ne sera envoyé.
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Optional: admin can override with a custom delay */}
                        <details className="text-sm">
                          <summary className="cursor-pointer text-[#C62E2E] font-medium select-none">
                            Besoin de modifier l'heure ? Saisir un délai à la place ↓
                          </summary>
                          <div className="mt-3 space-y-3">
                            <div className="grid grid-cols-5 gap-2">
                              {[
                                { mins: 30, label: '30 min' },
                                { mins: 45, label: '45 min' },
                                { mins: 60, label: '1 h' },
                                { mins: 75, label: '1 h 15' },
                                { mins: 90, label: '1 h 30' },
                              ].map(({ mins, label }) => (
                                <Button
                                  key={mins}
                                  variant={estimatedDelay === String(mins) ? 'default' : 'outline'}
                                  onClick={() => setEstimatedDelay(String(mins))}
                                  className={estimatedDelay === String(mins) ? 'bg-[#C62E2E]' : ''}
                                  size="sm"
                                  data-testid={`preset-delay-${mins}`}
                                >
                                  {label}
                                </Button>
                              ))}
                            </div>
                            <Input
                              type="number"
                              min="1"
                              max="240"
                              value={estimatedDelay}
                              onChange={(e) => setEstimatedDelay(e.target.value)}
                              placeholder="Ou délai personnalisé en minutes"
                            />
                          </div>
                        </details>
                      </>
                    ) : (
                      // ===== ASAP / DELAY UPDATE =====
                      <>
                        <p className="text-sm text-[#57534E]">
                          {isDelayUpdate
                            ? "Indiquez le nouveau délai estimé. Un email + SMS sera envoyé au client pour le prévenir."
                            : confirmTarget?.order_type === 'livraison'
                              ? "Le client a demandé \"le plus vite possible\". Indiquez dans combien de temps la commande sera livrée."
                              : "Le client a demandé \"le plus vite possible\". Indiquez dans combien de temps la commande sera prête."}
                        </p>

                        {/* Quick presets */}
                        <div className="grid grid-cols-5 gap-2">
                          {[
                            { mins: 30, label: '30 min' },
                            { mins: 45, label: '45 min' },
                            { mins: 60, label: '1 h' },
                            { mins: 75, label: '1 h 15' },
                            { mins: 90, label: '1 h 30' },
                          ].map(({ mins, label }) => (
                            <Button
                              key={mins}
                              variant={estimatedDelay === String(mins) ? 'default' : 'outline'}
                              onClick={() => setEstimatedDelay(String(mins))}
                              className={estimatedDelay === String(mins) ? 'bg-[#C62E2E]' : ''}
                              data-testid={`preset-delay-${mins}`}
                            >
                              {label}
                            </Button>
                          ))}
                        </div>

                        <div>
                          <Label>Ou délai personnalisé (en minutes)</Label>
                          <Input
                            type="number"
                            min="1"
                            max="240"
                            value={estimatedDelay}
                            onChange={(e) => setEstimatedDelay(e.target.value)}
                            placeholder="ex. 45"
                            className="mt-1"
                            data-testid="custom-delay-input"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => { setShowConfirmModal(false); setConfirmTarget(null); }}
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={submitConfirmation}
                      className={isDelayUpdate ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'}
                      disabled={(!useDesiredTimeFlow || isDelayUpdate) ? (!estimatedDelay || parseInt(estimatedDelay, 10) <= 0) : false}
                      data-testid="confirm-order-submit-btn"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      {isDelayUpdate ? 'Mettre à jour & informer client' : 'Confirmer & informer client'}
                    </Button>
                  </DialogFooter>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
